// Compact schema index for the chat system prompt.
//
// The full DescribeSchema payload is ~440 KB (about 110k tokens), three
// quarters of it field lists whose descriptions are placeholders. The
// prompt now carries an index instead: every entity with its one-line
// description, field names for the core entities general analytics
// questions use plus the execution and conversation entities an
// attribution question needs, every edge, and a handful of complete
// example specs. Field lists for the rest are one schema_describe_entity
// call away.

export const CORE_FIELD_ENTITIES = new Set([
    "ENTITY_TYPE_ENDPOINT",
    "ENTITY_TYPE_AGENT_INVENTORY",
    "ENTITY_TYPE_AI_SESSION",
    "ENTITY_TYPE_PROMPT",
    "ENTITY_TYPE_TOKEN",
    "ENTITY_TYPE_IDENTITY_OBSERVATION",
    "ENTITY_TYPE_RESOURCE_TRIE",
    "ENTITY_TYPE_ILO_EVENT_CLUSTER",
    "ENTITY_TYPE_ILO_CLUSTER",
    "ENTITY_TYPE_ILO_CLUSTER_GENERATION",
    "ENTITY_TYPE_ENDPOINT_METADATA_CHANGE",
    "ENTITY_TYPE_SIGNAL_TAGS",
    "ENTITY_TYPE_AI_AGENT_EXECUTION",
    "ENTITY_TYPE_ENDPOINT_USER_ASSIGNMENT",
    "ENTITY_TYPE_DIRECTORY_USER",
    "ENTITY_TYPE_ENDPOINT_STATUS",
    "ENTITY_TYPE_AI_SERVICE_OBSERVATION",
    "ENTITY_TYPE_AI_SERVICE_OBSERVATION_ENDPOINT_RECENCY",
    "ENTITY_TYPE_PROMPT_SEARCH",
    // Execution records: the only time-accurate, fleet-searchable account
    // of what an agent actually ran, and the anchor for attribution.
    "ENTITY_TYPE_PROCESS_EVENT",
    "ENTITY_TYPE_TELEMETRY_TIMELINE",
    "ENTITY_TYPE_TRACE_TOOL_ACTIVITY",
    // Conversation evidence: the model's own call that drove an execution.
    "ENTITY_TYPE_CONVERSATION_TRACE",
    "ENTITY_TYPE_CONVERSATION_MESSAGE",
    "ENTITY_TYPE_CONVERSATION_TOOL_CALL",
    "ENTITY_TYPE_CONVERSATION_SEARCH_CONTENT",
]);

// Examples worth carrying in full: one per spec shape (record read,
// bucketed group-by, join, cluster subtree, text search, trace hop).
const FULL_EXAMPLE_HINTS = [
    /list endpoints/i,
    /per-day total vs scaffolding/i,
    /input tokens by client surface/i,
    /under a cluster subtree/i,
    /telemetry index/i,
    /conversation trace into typed os process/i,
];

export const MAX_FULL_EXAMPLES = 6;

function strip(value, prefix) {
    return String(value || "").replace(prefix, "");
}

function timeFieldOf(entity) {
    const tf = entity.timeField || entity.time_field;
    if (!tf || /UNSPECIFIED$/.test(tf)) return null;
    return tf;
}

export function pickFullExamples(examples) {
    const chosen = [];
    for (const hint of FULL_EXAMPLE_HINTS) {
        const match = (examples || []).find((x) => hint.test(x.description || "") && !chosen.includes(x));
        if (match) chosen.push(match);
        if (chosen.length >= MAX_FULL_EXAMPLES) break;
    }
    for (const x of examples || []) {
        if (chosen.length >= MAX_FULL_EXAMPLES) break;
        if (!chosen.includes(x)) chosen.push(x);
    }
    return chosen;
}

export function buildSchemaIndexPrompt(schema) {
    const entities = schema?.entities || [];
    const edges = schema?.edges || [];
    const examples = schema?.examples || [];

    const entityLines = entities.map((e) => {
        const name = e.entity || e.name;
        const tf = timeFieldOf(e);
        const fields = e.fields || [];
        const head = `- ${name} (${fields.length} fields${tf ? `, time: ${tf}` : ", no time field"}): ${String(e.description || "").trim()}`;
        if (!CORE_FIELD_ENTITIES.has(name)) return head;
        const names = fields.map((f) => strip(f.field || f.name, "DIMENSION_FIELD_") + (f.wideColumn || f.wide_column ? "*" : ""));
        return `${head}\n    fields: ${names.join(", ")}`;
    });

    const edgeLines = edges.map((ed) => `${strip(ed.source, "ENTITY_TYPE_")} -[${strip(ed.edge, "EDGE_TYPE_")}]-> ${strip(ed.target, "ENTITY_TYPE_")}`);

    const full = pickFullExamples(examples);
    const exampleBlocks = full.map((x) => `${x.description}\n${JSON.stringify(x.query || x.spec)}`);
    const otherDescriptions = examples.filter((x) => !full.includes(x)).map((x) => `- ${x.description}`);

    return [
        "The analytics graph for this tenant, as an index. Entity and edge names below are exact; field names are listed only for the core entities and are written without the DIMENSION_FIELD_ prefix (add it back in specs; '*' marks a wide text column). For any other entity call `schema_describe_entity(ENTITY_TYPE_...)` to get its fields, edges, and examples before writing an analytics_query spec, or `schema_search(term)` when you only know a concept.",
        "",
        "<entities>",
        ...entityLines,
        "</entities>",
        "",
        "<edges>",
        ...edgeLines,
        "</edges>",
        "",
        "<examples>",
        "Complete runnable specs (the protobuf JSON analytics_query expects):",
        ...exampleBlocks.flatMap((b) => [b, ""]),
        otherDescriptions.length ? "Further examples, available through schema_describe_entity on the entity they name:" : "",
        ...otherDescriptions,
        "</examples>",
    ].join("\n");
}
